<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── HEADER ── -->
      <div class="wm-row">
        <div class="wm-line"></div>
        <span class="wm-text">Club Trailz</span>
        <div class="wm-line r"></div>
      </div>

      <button class="back-link" @click="navigateBack">
        <span class="back-arrow">←</span> Back
      </button>

      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Legal</span>
        <div class="section-rule"></div>
      </div>
      <h1 class="page-title">Privacy <em>&amp; Data Use</em></h1>
      <p class="page-sub">Invite-only. Member-first. Your data stays yours. · Last updated {{ lastUpdated }}</p>

      <!-- ── PLEDGE ── -->
      <div class="pledge">
        <div class="pledge-tick"></div>
        <div class="pledge-body">
          <div class="pledge-title">Our Data Promise</div>
          <p class="pledge-text">
            <strong>We never sell your personal data — full stop.</strong> Not today, not
            later, not as an "exception." Our model is membership, not monetizing your
            information.
          </p>
        </div>
      </div>

      <!-- ── DOCUMENT ── -->
      <div class="doc">

        <section class="doc-section">
          <h2 class="doc-h2">1. Our Core Principles</h2>
          <div class="pillars">
            <div v-for="(pillar, index) in corePillars" :key="index" class="pillar">
              <div class="pillar-mark"></div>
              <div>
                <h3 class="pillar-title">{{ pillar.title }}</h3>
                <p class="pillar-desc">{{ pillar.description }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">2. What We Collect</h2>
          <p class="doc-p">Just enough to serve you effectively — nothing more.</p>
          <ul class="doc-list">
            <li v-for="(item, index) in collectionItems" :key="index">
              <strong>{{ item.title }}:</strong> {{ item.description }}
            </li>
          </ul>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">3. How We Use It</h2>
          <p class="doc-p">Transparent practices, no surprises.</p>
          <ul class="doc-list">
            <li v-for="(usage, index) in usageItems" :key="index" v-html="usage.text"></li>
          </ul>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">4. Frequently Asked Questions</h2>
          <div class="faq-list">
            <div v-for="(faq, index) in faqItems" :key="index" class="faq-item">
              <button class="faq-q" @click="toggleFaq(index)">
                <span>{{ faq.question }}</span>
                <span class="faq-caret" :class="{ open: openFaqIndex === index }">＋</span>
              </button>
              <div v-if="openFaqIndex === index" class="faq-a" v-html="faq.answer"></div>
            </div>
          </div>
        </section>

      </div>

      <!-- ── CONTACT ── -->
      <div class="contact-block">
        <h3 class="contact-title">Questions About Privacy?</h3>
        <p class="contact-text">We're here to help. Reach out with any concerns.</p>
        <a class="btn-close" href="mailto:ricosevere@codeforgedev.com">
          <div class="btn-tick"></div>
          <span>Contact Privacy Team</span>
          <div class="btn-tick"></div>
        </a>
      </div>

      <div class="footer-links">
        <router-link :to="{ name: 'terms' }" class="footer-link">Terms of Service</router-link>
        <span class="footer-sep">·</span>
        <router-link to="/" class="footer-link">Back to Home</router-link>
      </div>
      <div class="footer-copyright">© {{ currentYear }} Club Trailz. All rights reserved.</div>

    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyView",

  data() {
    return {
      openFaqIndex: null,

      corePillars: [
        {
          title: "Security by Design",
          description: "End-to-end encryption, restricted access controls, and role-based permissions protect your data at every layer.",
        },
        {
          title: "Privacy by Default",
          description: "We collect only what's necessary. Your profile is private by default, and you control all visibility settings.",
        },
        {
          title: "Member Commitment",
          description: "Strict community standards and confidential spaces ensure your information stays within trusted circles.",
        },
      ],

      collectionItems: [
        { title: "Email & Basic Profile", description: "For authentication and essential account communications." },
        { title: "Professional Details", description: "Information you provide to enable curated, high-signal connections." },
        { title: "Security Metadata", description: "Essential logs to protect your account. Never used for advertising." },
      ],

      usageItems: [
        { text: "Authenticate you and maintain secure account access" },
        { text: "Suggest relevant members, chapters, and conversations" },
        { text: "Send service updates and notifications you opt into" },
        { text: "<strong>We do not sell, rent, or trade your data with third parties</strong>" },
        { text: "Share data only when you explicitly request it (e.g., connecting with members)" },
      ],

      faqItems: [
        {
          question: "Do you sell or rent member data?",
          answer: "<strong>Absolutely not.</strong> We never sell, rent, or trade your personal data. Our revenue comes exclusively from membership, not from advertising or data brokerage.",
        },
        {
          question: "Who can see my profile?",
          answer: "Only approved members of Club Trailz. You have full control over what information is visible and can adjust your privacy settings at any time.",
        },
        {
          question: "Where is my data stored?",
          answer: "We use industry-leading cloud providers with encryption both in transit and at rest. All access is role-restricted, logged, and monitored for security.",
        },
        {
          question: "How long do you keep my data?",
          answer: "Only as long as necessary to provide our services. You can request complete data deletion at any time. We retain only minimal records if required by law.",
        },
        {
          question: "Can I delete my account and data?",
          answer: "Yes, absolutely. You can delete your account at any time from your settings — it takes effect immediately, removing your profile, recommendations, travel plans, messages, and connections right away, except for minimal records required by law.",
        },
        {
          question: "How do you protect my data?",
          answer: "We employ multi-layered security including encryption, secure authentication, regular security audits, restricted access controls, and continuous monitoring for threats.",
        },
      ],
    };
  },

  computed: {
    lastUpdated() {
      return new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    },
    currentYear() {
      return new Date().getFullYear();
    },
  },

  methods: {
    toggleFaq(index) {
      this.openFaqIndex = this.openFaqIndex === index ? null : index;
    },
    navigateBack() {
      if (window.history.length > 2) {
        this.$router.go(-1);
      } else {
        this.$router.push("/");
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 720px; margin: 0 auto; padding: 56px 24px 100px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── WORDMARK ── */
.wm-row { display: flex; align-items: center; gap: 14px; margin-bottom: 32px; }
.wm-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2)); }
.wm-line.r { background: linear-gradient(90deg, rgba(210,175,100,0.2), transparent); }
.wm-text { font-size: 11px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.5); text-transform: uppercase; white-space: nowrap; }

/* ── BACK LINK ── */
.back-link { display: inline-flex; align-items: center; gap: 8px; background: none; border: none; color: rgba(237,232,223,0.4); font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 0.5px; cursor: pointer; padding: 0; margin-bottom: 32px; transition: color 0.3s; }
.back-link:hover { color: rgba(210,175,100,0.8); }
.back-arrow { font-size: 14px; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); flex: 0 0 24px; }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.page-title { font-family: 'Cormorant', serif; font-size: 44px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 10px; line-height: 1.1; }
.page-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.page-sub { font-size: 12px; font-weight: 500; letter-spacing: 1px; color: rgba(237,232,223,0.25); text-transform: uppercase; margin-bottom: 40px; }

/* ── PLEDGE ── */
.pledge { display: flex; gap: 18px; border: 1px solid rgba(210,175,100,0.25); background: rgba(210,175,100,0.04); padding: 22px 24px; margin-bottom: 48px; }
.pledge-tick { width: 3px; flex-shrink: 0; background: rgba(210,175,100,0.5); }
.pledge-title { font-family: 'Cormorant', serif; font-size: 19px; color: #edeae2; margin-bottom: 6px; }
.pledge-text { font-size: 13px; font-weight: 300; line-height: 1.7; color: rgba(237,232,223,0.6); margin: 0; }
.pledge-text strong { color: rgba(237,232,223,0.85); font-weight: 500; }

/* ── DOCUMENT ── */
.doc { display: flex; flex-direction: column; gap: 36px; margin-bottom: 48px; }
.doc-section { border-top: 1px solid rgba(210,175,100,0.08); padding-top: 28px; }
.doc-section:first-child { border-top: none; padding-top: 0; }
.doc-h2 { font-family: 'Cormorant', serif; font-size: 24px; font-weight: 400; color: #edeae2; margin-bottom: 14px; letter-spacing: -0.2px; }
.doc-p { font-size: 14px; font-weight: 300; line-height: 1.85; color: rgba(237,232,223,0.55); margin-bottom: 16px; }
.doc-list { display: flex; flex-direction: column; gap: 12px; padding-left: 20px; }
.doc-list li { font-size: 14px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.5); list-style: none; position: relative; }
.doc-list li::before { content: ''; position: absolute; left: -20px; top: 9px; width: 5px; height: 5px; border-radius: 50%; background: rgba(210,175,100,0.5); }
.doc-list li strong { color: rgba(237,232,223,0.8); font-weight: 500; }

/* ── PILLARS ── */
.pillars { display: flex; flex-direction: column; gap: 20px; }
.pillar { display: flex; gap: 16px; }
.pillar-mark { width: 5px; height: 5px; border-radius: 50%; background: rgba(210,175,100,0.5); flex-shrink: 0; margin-top: 8px; }
.pillar-title { font-size: 15px; font-weight: 500; color: rgba(237,232,223,0.85); margin-bottom: 4px; }
.pillar-desc { font-size: 13px; font-weight: 300; line-height: 1.7; color: rgba(237,232,223,0.5); margin: 0; }

/* ── FAQ ── */
.faq-list { display: flex; flex-direction: column; gap: 10px; }
.faq-item { border: 1px solid rgba(210,175,100,0.15); background: rgba(210,175,100,0.02); }
.faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; background: none; border: none; text-align: left; padding: 16px 18px; color: rgba(237,232,223,0.85); font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 500; cursor: pointer; }
.faq-caret { font-size: 15px; color: rgba(210,175,100,0.5); transition: transform 0.25s; flex-shrink: 0; }
.faq-caret.open { transform: rotate(45deg); }
.faq-a { padding: 0 18px 18px; font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.55); }
.faq-a >>> strong { color: rgba(237,232,223,0.8); font-weight: 500; }

/* ── CONTACT ── */
.contact-block { text-align: center; padding: 36px 0; border-top: 1px solid rgba(210,175,100,0.08); margin-bottom: 24px; }
.contact-title { font-family: 'Cormorant', serif; font-size: 22px; color: #edeae2; margin-bottom: 8px; }
.contact-text { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.5); margin-bottom: 24px; }

/* ── CLOSE-STYLE BUTTON ── */
.btn-close { width: 100%; display: flex; align-items: center; justify-content: center; gap: 14px; border: 1px solid rgba(210,175,100,0.3); background: transparent; padding: 16px; color: rgba(210,175,100,0.8); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; text-decoration: none; transition: border-color 0.3s, background 0.3s; box-sizing: border-box; }
.btn-close:hover { border-color: rgba(210,175,100,0.6); background: rgba(210,175,100,0.06); }
.btn-tick { width: 18px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }

/* ── FOOTER ── */
.footer-links { display: flex; align-items: center; justify-content: center; gap: 14px; margin-bottom: 8px; }
.footer-link { color: rgba(210,175,100,0.6); font-size: 12px; text-decoration: none; }
.footer-link:hover { color: rgba(210,175,100,0.9); }
.footer-sep { color: rgba(237,232,223,0.2); }
.footer-copyright { text-align: center; font-size: 11px; color: rgba(237,232,223,0.25); }

@media (max-width: 600px) { .page { padding: 40px 16px 80px; } .frame, .frame-inner { inset: 12px; } .page-title { font-size: 32px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>